/**
 * Reverse self-check — the two-step "did you know it?" flow (phrase-drill design §6).
 *
 * Typing English is a spelling test in a language the learner already speaks, so the passive
 * direction tests RECOGNITION instead. This module holds the parts of that flow with no DOM in
 * them — which step follows which, what each step's buttons mean, and how long step 1 gets — so
 * they can be unit-tested standalone (temp/ui-smoke/self-check-unit.mjs). training_session.js owns
 * the markup, the events and the SRS posting.
 *
 * The two steps are the whole point and must not be collapsed into one flashcard reveal: the
 * learner COMMITS ("I understand" / "I don't understand") before seeing the answer, and only then
 * grades themselves. Revealing first lets a learner tell themselves they knew it.
 */
(function (global) {
	"use strict";

	// Step 1 — before the reveal. "Do you know what this means?"
	var STEP_COMMIT = "commit";
	// Step 2 — after the reveal, reached only from "I understand". "Were you right?"
	var STEP_GRADE = "grade";

	/**
	 * Reading allowance, seconds per character of cue. Shared with the Practice gap frame's
	 * constant on purpose — it is the same quantity (how long a line of text takes to read) and
	 * two numbers for it would drift apart. Deliberately NOT scaled by the speed setting: §6.1's
	 * argument is that speeding the drill up should shorten the thinking, not make a nine-word
	 * sentence unreadable.
	 */
	var READ_SEC_PER_CHAR = 0.05;

	/**
	 * The recall half, expressed as an equivalent number of LETTERS rather than as seconds. That
	 * is what lets it ride the drill's existing speed dial (seconds-per-letter) instead of needing
	 * a second, parallel speed scale: recall is not length-dependent, but it should still speed up
	 * when the learner asks the drill to go faster. Eight is a short word's worth of thinking.
	 */
	var RECALL_LETTERS = 8;

	/** The flat base every drill timer starts from, matching the typed formula's leading 1 s. */
	var BASE_SEC = 1;

	/**
	 * Does this card use the self-check flow? Reverse only — the forward direction asks the
	 * learner to PRODUCE German, which a button cannot evidence. Dictation is excluded for the
	 * same reason it is excluded from the gap cue: writing out what was heard is the exercise.
	 *
	 * `enabled` is the §7 setting, which is deliberately still open; training_session.js passes
	 * the settled default (on) as a constant until that discussion lands.
	 */
	function applies(card, enabled) {
		return !!(enabled && card && card.reverse && !card.audioCue);
	}

	/**
	 * Step 1's allowance, in seconds: a reading term over the cue plus a fixed recall term on the
	 * speed dial. The clock covers THINKING only and stops at the reveal — grading yourself is not
	 * a timed task, and putting step 2 on a clock would reward the fast lie over the honest pause.
	 */
	function answerTime(cueLen, secPerLetter) {
		return BASE_SEC + secPerLetter * RECALL_LETTERS + READ_SEC_PER_CHAR * (cueLen || 0);
	}

	/**
	 * The buttons for a step, in display order. `n` is the digit that clicks it — the Learn
	 * ladder's idiom, so a pass is two keypresses and the hands never leave the keyboard. `pass`
	 * is what the button asserts, and it is only ever meaningful at STEP_GRADE: at STEP_COMMIT
	 * "I understand" does not claim a pass, it asks to see the answer.
	 */
	function buttons(step) {
		if (step === STEP_GRADE) {
			return [
				{n: 1, key: "right", res: "tsSelfRight", pass: true, cls: "_selfRightClass"},
				{n: 2, key: "wrong", res: "tsSelfWrong", pass: false, cls: "_selfWrongClass"}
			];
		}
		return [
			{n: 1, key: "know", res: "tsSelfKnow", pass: null, cls: "_selfKnowClass"},
			{n: 2, key: "dontKnow", res: "tsSelfDontKnow", pass: false, cls: "_selfWrongClass"}
		];
	}

	/**
	 * What pressing `key` at `step` does. Exactly one answer in the scheme reveals without
	 * deciding anything — "I understand" at step 1 — and every other press ends the card.
	 *
	 * The outcomes are deliberately asymmetric. "I don't understand" and "I got it wrong" are
	 * full-weight failures; only the pass is discounted (by α_self, server-side). Confidently
	 * wrong is worse than knowing you do not know, so the honest admission must never cost more
	 * than the silent one — which is why "wrong" and "dontKnow" resolve identically here.
	 */
	function press(step, key) {
		if (step === STEP_COMMIT && key === "know") {
			return {next: STEP_GRADE, reveal: true, done: false, pass: null};
		}
		if (step === STEP_GRADE && key === "right") {
			return {next: null, reveal: false, done: true, pass: true};
		}
		return {next: null, reveal: step === STEP_COMMIT, done: true, pass: false};
	}

	/** A step-1 timeout means "I don't understand" (§6.1) — the same full-weight failure. */
	function timeout() {
		return press(STEP_COMMIT, "dontKnow");
	}

	global.SelfCheck = {
		STEP_COMMIT: STEP_COMMIT,
		STEP_GRADE: STEP_GRADE,
		READ_SEC_PER_CHAR: READ_SEC_PER_CHAR,
		RECALL_LETTERS: RECALL_LETTERS,
		applies: applies,
		answerTime: answerTime,
		buttons: buttons,
		press: press,
		timeout: timeout
	};
})(typeof window !== "undefined" ? window : globalThis);
